import { isObject, randomId } from '@shared/utils';
import { post as apiPost } from '@/redux/requests/api.js';
import setRequestLoaderState from '@/redux/requests/requestLoader.js';
import buildOptimisticMutationMeta from '@/redux/requests/lib/buildOptimisticMutationMeta.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';
import { store } from '@/redux/lib/configureStore.js';

const COLLAPSE_FLUSH_WINDOW_MS = 200;
const MUTATION_BY_METHOD = Object.freeze({
  POST: 'create',
  PUT: 'update',
  DELETE: 'delete'
});

const collabQueueState = {
  pendingEntries: [],
  flushTimerId: null,
  inFlightPromise: null
};

const buildCollabDedupeKey = ({ resourceType, resourceId, mutation, payload } = {}) => {
  const normalizedResourceType = String(resourceType || '')
    .trim()
    .toLowerCase();
  const normalizedResourceId = String(resourceId || '').trim();
  const normalizedMutation = String(mutation || '')
    .trim()
    .toLowerCase();

  if (!normalizedResourceType || !normalizedResourceId || !normalizedMutation) {
    return null;
  }

  const baseKey = `${normalizedResourceType}:${normalizedResourceId}:${normalizedMutation}`;

  // Payloads addressed by a top-level `path` (e.g. userpref `{path, value}`)
  // treat each distinct path as a separate change. Without the path in the
  // dedupe key, two same-window writes at different paths collide and the
  // merge collapses both into one item (mergeCollabPayloadValues falls back to
  // "nextValue wins" on scalar keys), so all earlier paths get dropped before
  // the batch ships. Including the path keeps distinct-path writes as their
  // own entries while same-path writes still collapse (e.g. slider drags).
  if (payload && typeof payload === 'object' && typeof payload.path === 'string' && payload.path) {
    return `${baseKey}:${payload.path}`;
  }

  return baseKey;
};

const mergeCollabPayloadValues = ({ currentValue, nextValue } = {}) => {
  if (isObject(currentValue) && isObject(nextValue)) {
    const allKeys = new Set([...Object.keys(currentValue), ...Object.keys(nextValue)]);
    const mergedValue = {};

    for (const key of allKeys) {
      mergedValue[key] = mergeCollabPayloadValues({
        currentValue: currentValue[key],
        nextValue: nextValue[key]
      });
    }

    return mergedValue;
  }

  return nextValue !== undefined ? nextValue : currentValue;
};

const toFailurePayload = ({ itemResponse, fallbackMessage = 'Collab request failed.' } = {}) => {
  return createRequestFailure({
    code: String(itemResponse?.code || '').trim() || 'COLLAB_REQUEST_FAILED',
    message: String(itemResponse?.message || fallbackMessage),
    data: itemResponse?.data && typeof itemResponse.data === 'object' ? itemResponse.data : undefined
  });
};

const toSuccessPayload = ({ itemResponse } = {}) => {
  const responseData = itemResponse?.data;
  const result = responseData && typeof responseData === 'object' && Object.prototype.hasOwnProperty.call(responseData, 'result') ? responseData.result : responseData;

  return {
    success: true,
    data: result !== undefined ? result : null
  };
};

const toActionPayload = ({ payload, meta } = {}) => {
  if (!meta || !payload || typeof payload !== 'object') {
    return payload;
  }

  return {
    ...payload,
    meta: {
      ...meta,
      ...(payload.meta || {})
    }
  };
};

const dispatchCollabResponse = ({ action, meta, callback, payload, error } = {}) => {
  if (action) {
    store.dispatch({
      type: error ? `${action}_FAIL` : `${action}_SUCCESS`,
      payload: toActionPayload({
        payload,
        meta
      })
    });
  }

  if (typeof callback === 'function') {
    callback(error ? undefined : payload?.data, error ? payload : undefined);
  }
};

const resolveEntryFailure = ({ entry, message = 'Failed to submit collab batch.' } = {}) => {
  const failure = {
    success: false,
    code: 'COLLAB_BATCH_REQUEST_FAILED',
    message
  };

  for (const waiter of entry?.waiters || []) {
    waiter(failure);
  }
};

const flushCollabQueue = async () => {
  if (collabQueueState.inFlightPromise) {
    return collabQueueState.inFlightPromise;
  }

  const pendingEntries = Array.isArray(collabQueueState.pendingEntries) ? collabQueueState.pendingEntries : [];

  if (pendingEntries.length < 1) {
    return {
      success: true,
      data: {
        skipped: true
      }
    };
  }

  if (collabQueueState.flushTimerId) {
    const timerHost = typeof window !== 'undefined' ? window : globalThis;
    timerHost.clearTimeout(collabQueueState.flushTimerId);
    collabQueueState.flushTimerId = null;
  }

  collabQueueState.pendingEntries = [];
  const authToken = pendingEntries.find((entry) => String(entry?.authToken || '').trim())?.authToken || undefined;

  collabQueueState.inFlightPromise = (async () => {
    const batchRes = await apiPost('/v1/pieve/collab/batches', {
      authToken,
      resolveAuthToken: pendingEntries.some((entry) => entry?.resolveAuthToken === false) ? false : undefined,
      data: {
        requestId: randomId(),
        items: pendingEntries.map((entry) => entry.item)
      }
    });
    const responseItems = Array.isArray(batchRes?.data?.items) ? batchRes.data.items : [];

    if (!batchRes?.success) {
      for (const entry of pendingEntries) {
        resolveEntryFailure({
          entry,
          message: batchRes?.message || 'Failed to submit collab batch.'
        });
      }

      return batchRes;
    }

    for (let index = 0; index < pendingEntries.length; index += 1) {
      const entry = pendingEntries[index];
      const itemResponse = responseItems[index] || {
        success: false,
        code: 'COLLAB_BATCH_ITEM_MISSING',
        message: 'Missing collab batch item response.'
      };

      for (const waiter of entry?.waiters || []) {
        waiter(itemResponse);
      }
    }

    return batchRes;
  })();

  try {
    return await collabQueueState.inFlightPromise;
  } finally {
    collabQueueState.inFlightPromise = null;

    const nextPendingEntries = Array.isArray(collabQueueState.pendingEntries) ? collabQueueState.pendingEntries : [];

    if (nextPendingEntries.length > 0 && !collabQueueState.flushTimerId) {
      const timerHost = typeof window !== 'undefined' ? window : globalThis;
      collabQueueState.flushTimerId = timerHost.setTimeout(() => {
        collabQueueState.flushTimerId = null;
        flushCollabQueue().catch(() => {});
      }, 0);
    }
  }
};

const enqueueCollabEntry = ({ item, authToken, resolveAuthToken = undefined } = {}) => {
  return new Promise((resolve) => {
    const nextEntry = {
      item,
      authToken,
      resolveAuthToken,
      waiters: [resolve],
      dedupeKey: buildCollabDedupeKey(item)
    };
    const pendingEntries = Array.isArray(collabQueueState.pendingEntries) ? collabQueueState.pendingEntries : [];

    if (nextEntry.dedupeKey) {
      const existingEntry = pendingEntries.find((entry) => entry?.dedupeKey === nextEntry.dedupeKey);

      if (existingEntry) {
        existingEntry.item = {
          ...existingEntry.item,
          ...item,
          payload: mergeCollabPayloadValues({
            currentValue: existingEntry.item?.payload,
            nextValue: item?.payload
          })
        };
        existingEntry.authToken = authToken;
        existingEntry.resolveAuthToken = resolveAuthToken;
        existingEntry.waiters.push(resolve);
      } else {
        pendingEntries.push(nextEntry);
      }
    } else {
      pendingEntries.push(nextEntry);
    }

    collabQueueState.pendingEntries = pendingEntries;

    if (!collabQueueState.flushTimerId && !collabQueueState.inFlightPromise) {
      const timerHost = typeof window !== 'undefined' ? window : globalThis;
      collabQueueState.flushTimerId = timerHost.setTimeout(() => {
        collabQueueState.flushTimerId = null;
        flushCollabQueue().catch(() => {});
      }, COLLAPSE_FLUSH_WINDOW_MS);
    }
  });
};

const collabRequest = async (method, resourceType, config = {}) => {
  const normalizedMethod = String(method || '')
    .trim()
    .toUpperCase();
  const normalizedResourceType = String(resourceType || '')
    .trim()
    .toLowerCase();
  const normalizedResourceId = String(config?.resourceId || '').trim() || null;
  const mutation = String(config?.mutation || MUTATION_BY_METHOD[normalizedMethod] || '')
    .trim()
    .toLowerCase();
  const resolvedMeta = config.action && mutation !== 'read' ? buildOptimisticMutationMeta({ meta: config.meta }) : config.meta;

  if (!normalizedResourceType || !mutation) {
    const failurePayload = toFailurePayload({
      itemResponse: {
        code: 'COLLAB_REQUEST_INVALID',
        message: 'resourceType and mutation are required for collab requests.'
      }
    });

    dispatchCollabResponse({
      action: config.action,
      meta: resolvedMeta,
      callback: config.callback,
      payload: failurePayload,
      error: true
    });

    return failurePayload;
  }

  if (config.loader) {
    setRequestLoaderState({
      key: config.loader,
      isLoading: true
    });
  }

  if (config.action && mutation !== 'read') {
    store.dispatch({ type: `${config.action}_OPTIMISTIC`, payload: toActionPayload({ payload: { ...config.data, resourceId: normalizedResourceId }, meta: resolvedMeta }) });
  }

  try {
    const itemResponse = await enqueueCollabEntry({
      authToken: config.authToken,
      resolveAuthToken: config.resolveAuthToken,
      item: {
        resourceType: normalizedResourceType,
        ...(normalizedResourceId ? { resourceId: normalizedResourceId } : {}),
        mutation,
        payload: config?.data && typeof config.data === 'object' && !Array.isArray(config.data) ? config.data : {}
      }
    });
    const success = itemResponse?.success === true;
    let payload = success ? toSuccessPayload({ itemResponse }) : toFailurePayload({ itemResponse });

    if (typeof config.onResponse === 'function') {
      const nextResponseShape =
        config.onResponse({
          status: success ? 200 : 400,
          data: payload,
          config: {
            resourceType: normalizedResourceType,
            resourceId: normalizedResourceId,
            mutation
          }
        }) || null;

      if (nextResponseShape?.data) {
        payload = nextResponseShape.data;
      }
    }

    dispatchCollabResponse({
      action: config.action,
      meta: resolvedMeta,
      callback: config.callback,
      payload,
      error: !success
    });

    if (config.eventKey && success) {
      store.dispatch({ type: 'uiSetEventKey', payload: { key: config.eventKey, timestamp: Date.now() } });
    }

    return payload;
  } finally {
    if (config.loader) {
      setRequestLoaderState({
        key: config.loader,
        isLoading: false
      });
    }
  }
};

export default collabRequest;
